import { useEffect, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';

import looplineLogo from '~src/images/logos/lls-loop.png';
import loopnowLogo from '~src/images/logos/loopnow-prod.png';
import { AttributeI } from '~src/interfaces/attributes.types';
import { CompanyI } from '~src/interfaces/company.interface';
import { ContentLocalesI } from '~src/interfaces/session.types';
import { StyledLink } from '~src/outer_modules/shared/tosAndDataPrivacyInfoBox/TosAndDataPrivacyInfoBox';
import { StoreI } from '~src/rootReducer';
import { routes } from '~src/routingConfig';
import { defaultBaseApi } from '~src/services/ApiController';
import { translateContent } from '~src/services/translationServices';
import {
  validatorEmail,
  validatorRequired,
} from '~src/services/validators';
import AvatarBox, { AvatarType } from '~src/shared/avatarBox/AvatarBox';
import LoginButton from '~src/shared/buttons/LoginButton';
import IconInput, { InputIconType } from '~src/shared/form/inputs/IconInput';
import SelectInput from '~src/shared/form/select/SelectInput';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import { composeValidators } from '~src/shared/reactFinalForm/validators';
import { fetchCycles } from '~src/shared/sidebar/actions/sidebarActions';
import { removeNotification, showNotification } from '~src/store/app/actions';
import { loginUser } from '~src/store/session/actions';
import { SelfSignupFormI } from '~src/store/session/interfaces';
import { FieldType } from '~src/types';
import { sleep, trimValues } from '~src/utils';
import * as SC from './selfSignUp.sc';

const SelfSignupForm = (props: RouteComponentProps<{ tenantId: string }>) => {
  const {
    match: { params },
  } = props;
  const [t] = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [attributes, setAttributes] = useState<AttributeI[]>([]);
  const [company, setCompany] = useState<CompanyI>();
  const [companyImageUrl, setCompanyImageUrl] = useState<string>();
  const [surveyTitle, setSurveyTitle] = useState<ContentLocalesI|undefined>();
  const [surveyInvitation, setSurveyInvitation] = useState<ContentLocalesI|undefined>();
  const [cycleId, setCycleId] = useState<string|undefined>();
  const [password, setPassword] = useState<string>('');

  const isAuthenticated = useSelector<StoreI, boolean>((s) => s.user.session.authStatus);

  const emailPlaceholder = t('x.login.input.placeholder.email');
  const renderField = ({ autoFocus, input, label, meta: { touched, error }, labelKey }: FieldType) => (
    <IconInput
      {...input}
      autoFocus={autoFocus}
      label={label}
      type={input.type as InputIconType}
      error={touched ? error : null}
      labelKey={labelKey}
    />
  );

  useEffect(() => {
    setPassword(Math.random().toString(36).substring(2, 15));
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const cycleId = urlParams.get('cycle') || undefined;
    setCycleId(cycleId);
    defaultBaseApi
      .getSelfSignupPayload(params.tenantId, cycleId)
      .then((response) => {
        setAttributes(response.data.selfAssignableAttributes);
        setCompany(response.data.company);
        setCompanyImageUrl(response.data.companyImageDataUrl);
        setIsEnabled(true);
        if (response.data.surveyInvitation) {
          setSurveyInvitation(response.data.surveyInvitation);
        }
        if (response.data.surveyName) {
          setSurveyTitle(response.data.surveyName);
        }
      })
      .catch((err) => {
        console.log('Error occurred during fetching team-segments', err);
        dispatch(removeNotification('x.loading_error.message'));
        setIsEnabled(false);
        throw err;
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [location.search]);

  const goToLogin = () => history.push('/login');

  // We don't have all the cycles available immediately after user signup. API return them with a
  // delay. We can't tell how long API is going to take. So we make a guess here about how long we
  // should wait. Otherwise user is redirected to `/questions` where they get first available cycle
  const MAX_CYCLE_WAIT_PERIOD = 5000;

  const pollForCycle = async (cycleId: string, retryCount = 0) => {
    const waitPeriod = 1000;
    const action = await dispatch(fetchCycles());
    const {
      response: { data: cycles },
    } = action;

    if (cycles.find((cycle) => cycle.id === cycleId)) {
      return;
    }

    if (retryCount * waitPeriod >= MAX_CYCLE_WAIT_PERIOD) {
      return;
    }

    await sleep(waitPeriod);
    return pollForCycle(cycleId, retryCount + 1);
  };

  const handleSubmit = async (signupFormValues: SelfSignupFormI) => {
    const signupData = trimValues({
      firstname: String(t('x.user.self.signup.dummy_firstname')),
      lastname: String(t('x.user.self.signup.dummy_lastname')),
      password: password,
      ...signupFormValues,
    })
    setIsSubmitting(true);
    try {
      await defaultBaseApi.selfSignUp(signupData, params.tenantId);
      // TS doesn't know it, but we need await in below line
      await dispatch(loginUser({
        identifier: signupData.email,
        password: signupData.password,
      }));
      if (cycleId) {
        await pollForCycle(cycleId);
        history.push(`${routes.answerQuestions.cycle}/${cycleId}`);
      }
    } catch (err) {
      console.log('Error during signing up', err);
      dispatch(
        showNotification({
          id: 'invalid_email',
          txtKey: 'x.error.signup.email.inuse',
          type: 'error',
        }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const buttonText = surveyTitle ? t('x.user.self.signup.start.survey') : t('x.user.self.signup.button');

  if (isLoading) return <LoadingSpinner />;

  if (!isLoading && !isEnabled)
    return (
      <SC.Container>
        <SC.SubText data-translation={'x.user.self.signup.no.permission'}>
          {t('x.user.self.signup.no.permission')}
        </SC.SubText>
      </SC.Container>
    );

  if (isAuthenticated && !isSubmitting) {
    return <Redirect to={routes.answerQuestions.questions} />;
  }

  return (
    <SC.Container>
      <SC.SelfSignUpContainer>
        <SC.HeaderContainer>
          <SC.HeaderMainInfo>
            <AvatarBox
              size={90}
              type={AvatarType.Company}
              name={company?.name}
              dataUrl={companyImageUrl}
              url={looplineLogo}
            />
            <SC.Title>{company?.name}</SC.Title>
          </SC.HeaderMainInfo>
          <SC.HeaderMetaData>
            <SC.MetaDescription data-translation={'x.user.self.signup.meta.description'}>
              {t('x.user.self.signup.meta.description')}
            </SC.MetaDescription>
            <SC.HeaderMetaImg src={loopnowLogo} />
          </SC.HeaderMetaData>
        </SC.HeaderContainer>
        <SC.FormContainer>
          <SC.DetailsInfo>
            {surveyTitle && (
              <>
                <SC.DetailsText>{translateContent(surveyTitle)}</SC.DetailsText>
                {surveyInvitation &&
                  <SC.DetailsDescription data-translation="x.user.self.signup.survey.invite.description">
                    {translateContent(surveyInvitation)}
                  </SC.DetailsDescription>
                }
              </>
            )}
          </SC.DetailsInfo>
          <Form onSubmit={handleSubmit}>
            {({ submitting, handleSubmit }) => (
              <>
                <Field
                  name="email"
                  label={emailPlaceholder}
                  type="email"
                  children={renderField}
                  validate={composeValidators([validatorRequired, validatorEmail])}
                  labelKey="x.login.input.placeholder.email"
                />
                <SC.AttributesSection>
                  {attributes.map((attr) => (
                    <Field
                      children={({ input }) => (
                        <SC.AttributesGroup key={attr.uuid}>
                          <SelectInput
                            t={t}
                            {...input}
                            optList={attr.attribute_options.map((attrOption) => ({
                              name: translateContent(attrOption.value),
                              value: attrOption.uuid,
                            }))}
                            label={translateContent(attr.name)}
                            labelKey="x.users.selfSignUp.Attributes"
                          />
                        </SC.AttributesGroup>
                      )}
                      key={attr.uuid}
                      name={`attributes.${attr.uuid}`}
                      validate={validatorRequired}
                    />
                  ))}
                </SC.AttributesSection>
                <SC.InfoContainer>
                  <SC.InfoText data-translation="x.user.self.signup.privacy.info.first.part x.user.self.signup.privacy.info.second.part">
                    {t('x.user.self.signup.privacy.info.first.part')}
                  </SC.InfoText>
                  <SC.InfoLink>
                    <StyledLink
                      target="_blank"
                      href={t('x.imprint.link')}
                      data-translation="x.signup.infos.imprint.title, x.imprint.link"
                    >
                      {t('x.signup.infos.imprint.title')}
                    </StyledLink>
                    <SC.InfoText>{t('x.user.self.signup.privacy.info.second.part')}</SC.InfoText>
                  </SC.InfoLink>
                </SC.InfoContainer>

                <SC.Footer>
                  <LoginButton
                    isLoading={submitting}
                    onClick={handleSubmit}>
                    <span data-translation="x.user.self.signup.button">{buttonText}</span>
                  </LoginButton>
                  <SC.ExistingLogin data-translation="x.user.self.signup.login.already.exists">
                    {t('x.user.self.signup.login.already.exists')}
                    <SC.LoginLink data-translation="x.signup.header.button.message" onClick={goToLogin}>
                      {t('x.signup.header.button.message')}
                    </SC.LoginLink>
                  </SC.ExistingLogin>
                </SC.Footer>
              </>
            )}
          </Form>
        </SC.FormContainer>
      </SC.SelfSignUpContainer>
    </SC.Container>
  );
};

const SelfSignup = (props: RouteComponentProps) => {
  const { match } = props;

  return (
    <Switch>
      <Route exact path={`${match.path}/:tenantId`} component={SelfSignupForm} />
      <Redirect to={'/'} />
    </Switch>
  );
};

export default SelfSignup;
